/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, Group, Stack, Text, rem, useMantineTheme } from '@mantine/core';
import type { ContextModalProps } from '@mantine/modals';
import type { OpenContextModal } from '@mantine/modals/lib/context';
import { useTranslation } from 'react-i18next';

import CopyButton from '@fai/ui/components/copy-button';
import { TextVariants } from '@fai/ui/components/typography/variants';
import { FooterlessModalFooter } from '@fai/ui/mantine-modal/helpers';

export type CopyModalType = OpenContextModal<Partial<CopyModalProps>>;

interface CopyModalProps {
  value: string;
  modalBody?: JSX.Element;
}

export default function CopyModal({ context, id, innerProps }: ContextModalProps<CopyModalProps>) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  return (
    <Stack gap={theme.pf.layout.xs}>
      {innerProps.modalBody}

      <Group
        bd={`1px solid ${theme.pf.color.border.neutral.default}`}
        bg={theme.pf.color.bg.neutral.subtle.base}
        h={rem(40)}
        gap={0}
        wrap="nowrap"
        style={{ borderRadius: theme.pf.radius.sm }}
      >
        <Text
          flex={1}
          c={theme.pf.color.fg.neutral.subtle}
          variant={TextVariants.BodyMd}
          px={theme.pf.layout.xs}
          style={{ overflow: 'hidden', whiteSpace: 'pre-wrap' }}
          truncate="end"
        >
          {innerProps.value}
        </Text>

        <CopyButton
          actionIconProps={{
            bg: 'transparent',
            style: { borderWidth: `0 0 0 ${rem(1)}`, borderRadius: 0 },
          }}
          size="lg"
          value={innerProps.value}
        />
      </Group>

      <FooterlessModalFooter>
        <Button theme="cloud" onClick={() => context.closeModal(id)}>
          {t('component.okay')}
        </Button>
      </FooterlessModalFooter>
    </Stack>
  );
}
